import type { HTMLAttributes } from 'react';
import { motion } from 'motion/react';
import { cn } from './utils';

export type ProgressTone = 'gold' | 'ink' | 'emerald' | 'rose';
export type ProgressSize = 'xs' | 'sm' | 'md';

export type ProgressBarProps = HTMLAttributes<HTMLDivElement> & {
  /** Current value, in `[0, max]`. */
  value: number;
  max?: number;
  tone?: ProgressTone;
  size?: ProgressSize;
  /** Animate the fill on mount. */
  animate?: boolean;
  /** Show the percentage / value to the right. */
  showValue?: boolean;
  /** Custom value formatter. Defaults to `${pct}%`. */
  formatValue?: (value: number, max: number) => string;
};

const SIZE_CLS: Record<ProgressSize, string> = {
  xs: 'h-1',
  sm: 'h-1.5',
  md: 'h-2',
};

const TONE_FILL: Record<ProgressTone, string> = {
  gold: 'bg-[linear-gradient(90deg,#DCBE85,#A0793D)]',
  ink: 'bg-ink-900',
  emerald: '',
  rose: '',
};

export function ProgressBar({
  value,
  max = 100,
  tone = 'gold',
  size = 'sm',
  animate = false,
  showValue = false,
  formatValue,
  className,
  ...rest
}: ProgressBarProps) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  const display = formatValue ? formatValue(value, max) : `${Math.round(pct)}%`;
  const fillStyle =
    tone === 'emerald'
      ? { background: 'var(--color-emerald)' }
      : tone === 'rose'
        ? { background: 'var(--color-rose)' }
        : undefined;
  return (
    <div className={cn('w-full', className)} {...rest}>
      <div className={cn('rounded-full bg-[rgba(112,94,64,0.10)] overflow-hidden', SIZE_CLS[size])}>
        {animate ? (
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className={cn('h-full', TONE_FILL[tone])}
            style={fillStyle}
          />
        ) : (
          <div
            className={cn('h-full', TONE_FILL[tone])}
            style={{ width: `${pct}%`, ...fillStyle }}
          />
        )}
      </div>
      {showValue && (
        <p className="mt-1 text-[10.5px] uppercase tracking-[0.18em] font-semibold text-ink-500 tabular text-right">
          {display}
        </p>
      )}
    </div>
  );
}
